import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/utils";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

const CLS = { sent: "text-emerald-400", retry: "text-amber-300", failed: "text-red-300", pending: "text-slate-400" };

export function AdminEmails({ useData }) {
  const { data, loading, reload } = useData("/admin/emails");
  const [busy, setBusy] = useState("");
  const [stats, setStats] = useState(null);
  const loadStats = () => api.get("/admin/emails/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { loadStats(); }, [data]);
  const retryAll = async () => {
    setBusy("all");
    try { const { data: r } = await api.post("/admin/emails/retry-all"); toast[r.sent ? "success" : "info"](`${r.sent} nachgesendet · ${r.remaining} weiterhin in Warteschlange`); reload(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(""); }
  };
  const resend = async (e) => {
    if (!window.confirm(`E-Mail „${e.subject}“ an ${e.to} jetzt erneut senden?`)) return;
    setBusy(e.id);
    try { const { data: r } = await api.post(`/admin/emails/${e.id}/resend`); toast[r.status === "sent" ? "success" : "error"](r.status === "sent" ? "Erfolgreich gesendet" : `Versuch: ${r.status} – ${r.attempt.error || ""}`); reload(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(""); }
  };
  if (loading) return <div className="text-slate-400">Lädt…</div>;
  return (
    <div data-testid="admin-emails">
      {stats && (
        <div className="eb-card p-4 mb-4 flex flex-wrap items-center gap-6" data-testid="email-stats">
          <div><div className="eb-label">Zustellquote (30 Tage)</div><div className={`font-head text-2xl font-bold ${stats.delivery_rate >= 95 ? "text-emerald-400" : stats.delivery_rate >= 80 ? "text-amber-300" : "text-red-300"}`} data-testid="email-delivery-rate">{stats.delivery_rate == null ? "–" : `${stats.delivery_rate} %`}</div></div>
          <div className="text-sm text-slate-300"><span className="text-emerald-400 font-semibold">{stats.sent}</span> gesendet · <span className="text-amber-300 font-semibold">{stats.retry}</span> in Wiederholung · <span className="text-red-300 font-semibold">{stats.failed}</span> fehlgeschlagen · {stats.total} gesamt</div>
          {stats.retry > 0 && <button onClick={retryAll} disabled={busy === "all"} className="eb-btn-outline !py-1.5 text-xs ml-auto" data-testid="email-retry-all"><RefreshCw className={`w-3.5 h-3.5 ${busy === "all" ? "animate-spin" : ""}`} /> Alle retry-Mails jetzt nachsenden</button>}
        </div>
      )}
      <div className="eb-card overflow-hidden">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-slate-800 text-left">{["Datum", "Typ", "An", "Betreff", "Status", "Versuche", ""].map((h) => <th key={h} className="px-4 py-3 eb-label">{h}</th>)}</tr></thead>
        <tbody>
          {data.map((e) => (
            <tr key={e.id} className="border-b border-slate-800/50 hover:bg-white/[0.02]" data-testid={`email-row-${e.id}`}>
              <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtDate(e.created_at)} {e.created_at?.slice(11, 16)}</td>
              <td className="px-4 py-3 text-slate-300">{e.kind}</td>
              <td className="px-4 py-3 text-slate-300">{e.to}</td>
              <td className="px-4 py-3 text-slate-300 max-w-xs truncate" title={e.subject}>{e.subject}</td>
              <td className={`px-4 py-3 font-semibold ${CLS[e.status] || ""}`} data-testid={`email-status-${e.id}`} title={e.error || ""}>{e.status}{e.error && <div className="text-[11px] font-normal text-slate-500 max-w-[220px] truncate">{e.error}</div>}</td>
              <td className="px-4 py-3 text-xs text-slate-400">{e.resend_attempts?.length ? e.resend_attempts.map((a, i) => <div key={i}>{fmtDate(a.at)} {a.at?.slice(11, 16)} → {a.result}</div>) : "–"}</td>
              <td className="px-4 py-3 text-right">{["retry", "failed"].includes(e.status) && <button onClick={() => resend(e)} disabled={busy === e.id} className="eb-btn-outline !py-1.5 text-xs whitespace-nowrap" data-testid={`email-resend-${e.id}`}><RefreshCw className={`w-3.5 h-3.5 ${busy === e.id ? "animate-spin" : ""}`} /> Jetzt erneut senden</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}
